import { IconButton, Modal, Table, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import axios from '../../util/Axios';
import { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import RemoveIcon from '@mui/icons-material/RemoveCircleOutline';
import CommentIcon from '@mui/icons-material/Comment';
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import TestDetails from '../../components/TestDetails/TestDetails';

import { RemoveFromDataSet } from './RemoveFromDataSet';
import { ActionMenu } from './ActionMenu';
import { asDate, toIsoDate } from '../../util/DateFormat';
import { DataSetCategories } from './DataSetCategories';

import { CopyDataSet } from './CopyDataSet';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import * as actions from '../../store/actions/GlobalActions';
import { UploadEvaluation } from './UploadEvaluation';

import { ScoreAccuracy } from './ScoreAccuracy';
import { DataSetAccuracy } from './DataSetAccuracy';
import { FilterSelect } from '../../components/FilterSelect';

const actionToolbarStyle = { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginRight: '16px', marginBottom: '16px' };

function affectedStatus(test) {
  const neurologicalExamination = test.neurologicalExamination;
  if (!neurologicalExamination) {
    return 'Invalid';
  }
  const examinationStatus = neurologicalExamination.ischemic || neurologicalExamination.hemorrhagic ? 'Affected' : 'Healthy';

  return examinationStatus;
}

export default function DataSet({ id, editable = true }) {
  const navigate = useNavigate();

  const [dataSet, setDataSet] = useState(undefined);

  const [tests, setTests] = useState([]);

  const [shouldRefresh, setShouldRefresh] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const [pageParam, setPageParam] = useState(0);

  const [page, setPage] = useState({ number: 0, count: 0 });

  const dispatch = useDispatch();

  const [hashTagFilter, setHashTagFilter] = useState('');

  const [hashTags, setHashTags] = useState([]);

  useEffect(() => {
    axios(`/dataSets/${id}/strokeTests/details/?${hashTagFilterParam(hashTagFilter)}&size=${rowsPerPage}&page=${pageParam}&sort=createdAt`).then((response) => {
      const tests = response.data.content;
      setTests(tests);
      setPage({ number: response.data.number, count: response.data.totalElements });
      setShouldRefresh(false);

      axios(`/dataSets/${id}`).then((response) => {
        setDataSet(response.data);
      });
    });
  }, [id, rowsPerPage, pageParam, shouldRefresh, hashTagFilter]);

  const handleExtractAudio = () => {
    axios.post(`/dataSets/${id}/extractAudioFiles`).then(() => {});
  };

  const handleExportToV7 = () => {
    axios.post(`/dataSets/${id}/v7/exportItems`).then(() => {});
  };

  const handleEvaluateArms = () => {
    axios.post(`/dataSets/${id}/predictions/arms`).then(() => {});
  };

  const handleEvaluateSpeech = () => {
    axios.post(`/dataSets/${id}/predictions/speech`).then(() => {});
  };

  const handleEvaluateFace = () => {
    axios.post(`/dataSets/${id}/predictions/face`).then(() => {});
  };

  const handleArchive = () => {
    axios.post(`/dataSets/${id}/archive`).then(() => {});
  };

  const handleRestore = () => {
    axios.post(`/dataSets/${id}/restore`).then(() => {});
  };

  const handleDownload = () => {
    axios.get(`/dataSets/${id}/export`).then((response) => {
      const data = new Blob([JSON.stringify(response.data)], { type: 'text/json' });
      const fileUrl = window.URL.createObjectURL(data);
      const tempLink = document.createElement('a');
      tempLink.href = fileUrl;
      tempLink.setAttribute('download', `${id}.json`);
      tempLink.click();
    });
  };

  const [openVideo, setOpenVideo] = useState(false);
  const [openedTestId, setOpenedTestId] = useState(null);
  const [selectedTestId, setSelectedTestId] = useState(null);

  useEffect(() => {
    axios(`/hashTags`).then((response) => {
      setHashTags(response.data.map((s) => ({ name: s, value: s })));
    });
  }, []);

  const handleChangeHashTagFilter = (event) => {
    const {
      target: { value },
    } = event;
    setHashTagFilter(value);
  };

  function hashTagFilterParam(hashTagFilter) {
    return hashTagFilter.length > 0 ? `${`&hashTag=${encodeURIComponent(hashTagFilter)}`}` : '';
  }

  const handleOpenVideo = (testId) => {
    setOpenedTestId(testId);
    setOpenVideo(true);
    setSelectedTestId(testId);
  };

  const handleCloseVideo = () => {
    setOpenVideo(false);
    setOpenedTestId(null);
    setShouldRefresh(true);
  };

  const [openRemoveFromSet, setOpenRemoveFromSet] = useState(false);

  const handleRemoveFromSet = (testId) => {
    setOpenedTestId(testId);
    setOpenRemoveFromSet(true);
  };

  const handleCloseRemoveFromSet = () => {
    setOpenedTestId(null);
    setOpenRemoveFromSet(false);
    setShouldRefresh(true);
  };

  const [openCopy, setOpenCopy] = useState(false);

  const handleCopy = () => {
    setOpenCopy(true);
  };

  const handleCloseCopy = () => {
    setOpenCopy(false);
    dispatch({ type: actions.REFRESH_DATA_SETS });
  };

  const [openDelete, setOpenDelete] = useState(false);
  const handleDelete = () => {
    setOpenDelete(true);
  };

  const handleCloseDelete = (confirmed) => {
    setOpenDelete(false);
    if (confirmed) {
      axios.delete(`/dataSets/${id}`).then(() => {
        navigate('/all');
        dispatch({ type: actions.REFRESH_DATA_SETS });
      });
    }
  };

  const [openUpdate, setOpenUpdate] = useState(false);
  const handleUpdate = () => {
    setOpenUpdate(true);
  };

  const handleCloseUpdate = (confirmed) => {
    setOpenUpdate(false);
    if (confirmed) {
      axios.post(`/dataSets/${id}/update`).then(() => {
        dispatch({ type: actions.REFRESH_DATA_SETS });
      });
    }
  };

  const [openUploadEvaluation, setOpenUploadEvaluation] = useState(false);
  const handleUploadEvaluation = () => {
    setOpenUploadEvaluation(true);
  };

  const handleCloseUploadEvaluation = (confirmed) => {
    setOpenUploadEvaluation(false);
    if (confirmed) {
      dispatch({ type: actions.REFRESH_DATA_SETS });
    }
  };

  return (
    <>
      {!!dataSet ? (
        <>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              fontSize: '2em',
              width: '100%',
            }}
          >
            {dataSet.name}
          </Typography>
          <DataSetAccuracy dataSet={dataSet} />

          <Typography
            variant="subtitle1"
            component="body"
            sx={{
              textAlign: 'center',
              width: '100%',
            }}
          >
            v{dataSet?.version}, updated {toIsoDate(asDate(dataSet?.updated))}
          </Typography>

          <div style={actionToolbarStyle}>
            {dataSet?.dataSetType ? <DataSetCategories tests={tests} dataSetType={dataSet.dataSetType} /> : ''}
            <FilterSelect filter={hashTagFilter} label="Filter by #" handleChangeFilter={handleChangeHashTagFilter} items={hashTags} />
            <Tooltip title="Dowload dataset">
              <IconButton onClick={handleDownload}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            {editable ? (
              <ActionMenu
                extractAudio={handleExtractAudio}
                exportToV7={handleExportToV7}
                copy={handleCopy}
                deleteDataSet={handleDelete}
                updateDataSet={handleUpdate}
                evaluateArms={handleEvaluateArms}
                evaluateFace={handleEvaluateFace}
                evaluateSpeech={handleEvaluateSpeech}
                uploadEvaluation={handleUploadEvaluation}
                archive={handleArchive}
                restore={handleRestore}
              />
            ) : (
              ''
            )}
          </div>
          <Table size="small" aria-label="purchases">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Examination Status</TableCell>
                <TableCell>Comment</TableCell>
                <TableCell>Details</TableCell>
                <TableCell>AI Accuracy</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tests.map((test) => (
                <TableRow key={test.uuid} sx={test.uuid === selectedTestId ? { backgroundColor: '#e3f2fd' } : {}}>
                  <TableCell>{test.uuid}</TableCell>
                  <TableCell>{affectedStatus(test)}</TableCell>
                  <TableCell>
                    {test.comment ? (
                      <Tooltip title={test.comment}>
                        <IconButton>
                          <CommentIcon></CommentIcon>
                        </IconButton>
                      </Tooltip>
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpenVideo(test.uuid)}>
                      <Tooltip title="View details">
                        <InfoIcon />
                      </Tooltip>
                    </IconButton>
                    {editable ? (
                      <IconButton onClick={() => handleRemoveFromSet(test.uuid)}>
                        <Tooltip title="Remove from data set">
                          <RemoveIcon />
                        </Tooltip>
                      </IconButton>
                    ) : (
                      ''
                    )}
                  </TableCell>
                  <TableCell>
                    <ScoreAccuracy dataSet={dataSet} test={test} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              {page?.count > 0 ? (
                <TableRow>
                  <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100, 1000]} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => setRowsPerPage(e.target.value)} count={page.count} page={page.number} onPageChange={(e, newPage) => setPageParam(newPage)} />
                </TableRow>
              ) : (
                ''
              )}
            </TableFooter>
          </Table>
          <Modal open={openVideo} onClose={handleCloseVideo}>
            <TestDetails id={openedTestId} onClose={handleCloseVideo}></TestDetails>
          </Modal>
          <RemoveFromDataSet dataSetId={id} dataSetName={dataSet?.name} testId={openedTestId} open={openRemoveFromSet} handleClose={handleCloseRemoveFromSet} />
          <CopyDataSet dataSetId={id} dataSetName={dataSet?.name} open={openCopy} handleClose={handleCloseCopy} />
          <ConfirmDialog open={openDelete} onClose={handleCloseDelete} title={`Delete ${dataSet?.name}`} text={`Are you sure you want to delete ${dataSet?.name}?`} />
          <ConfirmDialog open={openUpdate} onClose={handleCloseUpdate} title={`Update ${dataSet?.name}`} text={`The update will add all valid tests appropriate for this data set. Do you want to continue?`} />
          <UploadEvaluation dataSetId={id} open={openUploadEvaluation} handleClose={handleCloseUploadEvaluation} />
        </>
      ) : (
        ''
      )}
    </>
  );
}
